/**
 * Service Worker Update Manager
 * Handles service worker updates with grace period and multi-tab coordination
 */

import { snackBarRef } from '../stores/snackbarRef';
import intl from 'react-intl-universal';

interface UpdateState {
  hasUpdate: boolean;
  isWaiting: boolean;
  registration: ServiceWorkerRegistration | null;
  lastUpdateCheck: number;
  gracePeriodEnd: number;
}

class ServiceWorkerUpdateManager {
  private state: UpdateState;
  private readonly GRACE_PERIOD_MS = 24 * 60 * 60 * 1000; // 24 hours
  private readonly UPDATE_CHECK_INTERVAL = 60 * 60 * 1000; // 1 hour
  private updateCallbacks: Array<(hasUpdate: boolean) => void> = [];
  private isLeader = false;
  private readonly tabId = Date.now().toString(36) + Math.random().toString(36).slice(2);

  constructor() {
    this.state = {
      hasUpdate: false,
      isWaiting: false,
      registration: null,
      lastUpdateCheck: 0,
      gracePeriodEnd: 0
    };

    this.setupLeaderElection();
    this.setupUpdateChecking();
  }

  /**
   * Register callback for update notifications
   */
  onUpdateAvailable(callback: (hasUpdate: boolean) => void): void {
    this.updateCallbacks.push(callback);
  }

  /**
   * Check for service worker updates
   */
  async checkForUpdates(): Promise<void> {
    if (!('serviceWorker' in navigator)) return;

    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) return;

      this.state.registration = registration;
      this.state.lastUpdateCheck = Date.now();

      // Check for waiting service worker
      if (registration.waiting) {
        this.handleWaitingServiceWorker(registration.waiting);
        return;
      }

      // Check for installing service worker
      if (registration.installing) {
        this.trackInstalling(registration.installing);
        return;
      }

      // Manually check for updates
      await registration.update();
      
    } catch (error) {
      console.error('[SW-UPDATE] Failed to check for updates:', error);
    }
  }

  /**
   * Apply pending update
   */
  async applyUpdate(): Promise<void> {
    if (!this.state.registration?.waiting) {
      console.warn('[SW-UPDATE] No waiting service worker to activate');
      return;
    }

    try {
      // Send skip waiting message to the waiting service worker
      this.state.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      
      // Wait for the new service worker to take control
      await this.waitForControllerChange();
      
      // Reload the page to use the new service worker
      window.location.reload();
      
    } catch (error) {
      console.error('[SW-UPDATE] Failed to apply update:', error);
    }
  }

  /**
   * Force reload without service worker (for chunk load errors)
   * This bypasses the service worker to get fresh assets from the server
   */
  async forceReloadWithoutCache(): Promise<void> {
    try {
      // Unregister service worker to force fresh fetch
      if (this.state.registration) {
        await this.state.registration.unregister();
        console.log('[SW-UPDATE] Service worker unregistered for fresh reload');
      }
      
      // Clear all caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
        console.log('[SW-UPDATE] All caches cleared');
      }
      
      // Force reload with cache bypass
      window.location.reload();
      
    } catch (error) {
      console.error('[SW-UPDATE] Failed to force reload:', error);
      // Fallback to simple reload
      window.location.reload();
    }
  }

  /**
   * Dismiss the current update (will check again after grace period)
   */
  dismissUpdate(): void {
    this.state.hasUpdate = false;
    this.state.gracePeriodEnd = Date.now() + this.GRACE_PERIOD_MS;
    
    // Store grace period in localStorage for persistence across tabs
    localStorage.setItem('sw-update-grace-end', this.state.gracePeriodEnd.toString());
    
    // Close any existing update notification
    const currentSnackbarKey = (this as any).currentUpdateSnackbarKey;
    if (currentSnackbarKey && snackBarRef.current) {
      snackBarRef.current.closeSnackbar(currentSnackbarKey);
      (this as any).currentUpdateSnackbarKey = null;
    }
    
    this.notifyCallbacks();
    console.log('[SW-UPDATE] Update dismissed, grace period until:', new Date(this.state.gracePeriodEnd));
  }

  /**
   * Get current update state
   */
  getState(): UpdateState {
    return { ...this.state };
  }

  /**
   * Setup leader election for multi-tab coordination
   */
  private setupLeaderElection(): void {
    // Simple leader election using localStorage and storage events
    const leaderId = 'sw-update-leader';
    const heartbeatKey = 'sw-update-heartbeat';
    
    const becomeLeader = () => {
      this.isLeader = true;
      localStorage.setItem(leaderId, this.tabId);
      console.log('[SW-UPDATE] Became update leader');
    };

    const checkLeadership = () => {
      const currentLeader = localStorage.getItem(leaderId);
      const heartbeat = localStorage.getItem(heartbeatKey);

      if (!currentLeader || !heartbeat) {
        becomeLeader();
        return;
      }

      const heartbeatTime = parseInt(heartbeat);
      const now = Date.now();

      // Leader is considered dead if no heartbeat for 30 seconds
      if (now - heartbeatTime > 30000) {
        becomeLeader();
      } else {
        this.isLeader = currentLeader === this.tabId;
      }
    };

    // Initial leadership check
    checkLeadership();
    
    // Heartbeat every 10 seconds if leader
    setInterval(() => {
      if (this.isLeader) {
        localStorage.setItem(heartbeatKey, Date.now().toString());
      }
      checkLeadership();
    }, 10000);

    // Listen for storage events (other tabs)
    window.addEventListener('storage', (event) => {
      if (event.key === leaderId || event.key === heartbeatKey) {
        checkLeadership();
      }
    });
  }

  /**
   * Setup automatic update checking
   */
  private setupUpdateChecking(): void {
    // Check for updates on page load
    if (document.readyState === 'complete') {
      this.checkForUpdates();
    } else {
      window.addEventListener('load', () => this.checkForUpdates());
    }

    // Periodic update checks (only if leader)
    setInterval(() => {
      if (this.isLeader) {
        this.checkForUpdates();
      }
    }, this.UPDATE_CHECK_INTERVAL);

    // Check for updates when page becomes visible
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && this.isLeader) {
        const timeSinceLastCheck = Date.now() - this.state.lastUpdateCheck;
        if (timeSinceLastCheck > this.UPDATE_CHECK_INTERVAL) {
          this.checkForUpdates();
        }
      }
    });

    // Load grace period from localStorage
    const savedGracePeriod = localStorage.getItem('sw-update-grace-end');
    if (savedGracePeriod) {
      this.state.gracePeriodEnd = parseInt(savedGracePeriod);
    }
  }

  /**
   * Handle waiting service worker
   */
  private handleWaitingServiceWorker(worker: ServiceWorker): void {
    this.state.isWaiting = true;
    
    // Check if we're still in grace period
    if (Date.now() < this.state.gracePeriodEnd) {
      console.log('[SW-UPDATE] Update available but in grace period');
      return;
    }
    
    this.state.hasUpdate = true;
    this.notifyCallbacks();
    this.showUpdateNotification();
    
    console.log('[SW-UPDATE] Service worker update available');
  }

  /**
   * Show update notification to user
   */
  private showUpdateNotification(): void {
    // Only show notification if snackbar is available and we're the leader tab
    if (!snackBarRef.current || !this.isLeader) {
      return;
    }

    const message = intl.get('app.update.available').d('A new version of the app is available');
    const updateText = intl.get('app.update.action').d('Update Now');

    const snackbarKey = snackBarRef.current.enqueueSnackbar({
      message,
      variant: 'info',
      persist: true, // Keep notification visible until user acts
      actionDef: {
        text: updateText,
        onClick: () => {
          this.applyUpdate();
          snackBarRef.current?.closeSnackbar(snackbarKey);
        },
      },
    });

    // Store the snackbar key for potential dismissal
    (this as any).currentUpdateSnackbarKey = snackbarKey;
  }

  /**
   * Track installing service worker
   */
  private trackInstalling(worker: ServiceWorker): void {
    worker.addEventListener('statechange', () => {
      if (worker.state === 'installed') {
        this.handleWaitingServiceWorker(worker);
      }
    });
  }

  /**
   * Wait for controller change with timeout
   */
  private waitForControllerChange(): Promise<void> {
    return new Promise((resolve) => {
      const timeout = setTimeout(() => resolve(), 10000);
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        clearTimeout(timeout);
        resolve();
      }, { once: true });
    });
  }

  /**
   * Notify all callbacks about update state change
   */
  private notifyCallbacks(): void {
    this.updateCallbacks.forEach(callback => {
      try {
        callback(this.state.hasUpdate);
      } catch (error) {
        console.error('[SW-UPDATE] Callback error:', error);
      }
    });
  }
}

// Export singleton instance
export const serviceWorkerUpdateManager = new ServiceWorkerUpdateManager();
export default serviceWorkerUpdateManager;